import { useEffect } from 'react';
import { data, Outlet, useLoaderData, useMatches } from 'react-router';
import { type RequestContext, type SerializeFrom } from '@cx-web/domain-types';
import { AuthModalProvider } from '@cx-web/feature-auth-ui';
import { featureFlags, useFeatureFlag } from '@cx-web/feature-flags/provider';
import { UIConfigProvider } from '@cx-web/ui-shell/ui-config-context';
import { getRepubMetaFromRepub } from '@cx-web/utils';

import Footer from '~/components/BettingInsights/components/Footer';
import { Header } from '~/components/Header';
import { getRequestContext } from '~/utils/getRequestContext.server';

import type { Route } from './+types/layout';
import { useGZConsent } from './gz/hooks/useGZConsent';
import { useGZConsentGatedGTM } from './gz/hooks/useGZConsentGatedGTM';
import { useGZConsentGatedMetaPixel } from './gz/hooks/useGZConsentGatedMetaPixel';
import { useGZTabTracking } from './gz/hooks/useGZTabTracking';
import { initChildIframeContext } from './gz/scripts';
import stylesheet from './styles/tailwind.css?url';
import type { loader as rootLoader } from './root-loader';

function GZIframeChildInit() {
  useGZTabTracking();
  const { consentGranted } = useGZConsent();

  const matches = useMatches();
  const rootMatch = matches.find(match => /oddschecker-plus\/root$/.test(match.id));
  const rootLoaderData = rootMatch?.data as SerializeFrom<typeof rootLoader> | undefined;

  useGZConsentGatedGTM({
    consentGranted,
    geolocationInfo: rootLoaderData?.geolocationInfo,
    gtmId: rootLoaderData?.gtmId,
    userGroup: rootLoaderData?.myocData?.userGroup,
    userId: rootLoaderData?.myocData?.myOCUser?.userID,
  });

  useGZConsentGatedMetaPixel(consentGranted);

  useEffect(() => {
    initChildIframeContext();
    import('@iframe-resizer/child');
  }, []);

  return null;
}

export const links: Route.LinksFunction = () => [
  {
    rel: 'stylesheet',
    href: 'https://cdn.jsdelivr.net/npm/instantsearch.css@7/themes/satellite-min.css',
  },
  { rel: 'stylesheet', href: stylesheet },
];

export const loader = async ({ context, request }: Route.LoaderArgs) => {
  const url = new URL(request.url);
  const repub = context.repub;

  const requestContext = await getRequestContext.call(context, repub, request);

  return data({
    repub,
    requestContext,
    url,
  });
};

export default function OddscheckerPlus() {
  const matches = useMatches();
  const rootMatch = matches.find(match => /oddschecker-plus\/root$/.test(match.id));
  const rootLoaderData = rootMatch?.data as SerializeFrom<typeof rootLoader>;
  if (!rootLoaderData) {
    throw new Error('Root loader data is missing');
  }
  const { repubIs, repubPath } = getRepubMetaFromRepub(rootLoaderData.repub);

  const {
    ENV: { BETSLIP_SCRIPT_URL, OC_API_KEY, PROSPERSTACK_URL },
    baseUrl,
    deviceId,
    deviceType,
    geolocationInfo,
    isAdmiralCookieBannerEnabled,
    myocData,
    resourcesUrl,
    socialLoginIds,
    url: currentUrl,
  } = rootLoaderData;

  const { repub, requestContext } = useLoaderData<typeof loader>();
  const subscriptions = myocData?.subscriptions;

  const isTipstersEnabled = useFeatureFlag(featureFlags.SUBS__PLUS__TIPSTERS);

  return (
    <AuthModalProvider>
      <UIConfigProvider
        deviceType={deviceType}
        isAdmiralCookieBannerEnabled={isAdmiralCookieBannerEnabled}
        resourcesUrl={resourcesUrl}
      >
        <Header
          BETSLIP_SCRIPT_URL={BETSLIP_SCRIPT_URL}
          apiKey={OC_API_KEY[repub]}
          baseUrl={baseUrl}
          countryCode={geolocationInfo.countryCode}
          currentUrl={currentUrl}
          deviceId={deviceId}
          googleID={socialLoginIds?.googleID ?? ''}
          isTipstersEnabled={isTipstersEnabled}
          prosperStackUrl={PROSPERSTACK_URL}
          repub={rootLoaderData.repub}
          repubIs={repubIs}
          repubPath={repubPath}
          requestContext={requestContext as RequestContext}
          resourcesUrl={resourcesUrl}
          subscriptions={subscriptions}
        />
        <Outlet />
        {!repubIs.GZ && <Footer repubIs={repubIs} resourcesUrl={resourcesUrl} />}
        {repubIs.GZ && <GZIframeChildInit />}
      </UIConfigProvider>
    </AuthModalProvider>
  );
}
